import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Box, Group, Drawer, Button, SegmentedControl, Text, ActionIcon } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { safeValue } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import ModalResponse from "../../components/general/ModalResponse";

const StockCategoryEdit = ({ stateShow, fnShow, stateData, fnSuccessEdit }) => {

    const mountedRef = React.useRef(true);
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorSubmitData: 'stockCategoryEdit-1',
        successSubmitData: 'stockCategoryEdit-2',
    })

    const constStockCategoryName = {
        id: 'editStockCategoryName',
        label: 'Kategori Stok',
        withAsterisk: true,
        placeholder: 'Contoh: RAM 4GB',
        value: '',
        description: '',
        error: ''
    }

    const constStockCategoryIsActive = {
        id: 'editStockCategoryIsActive',
        label: 'Status',
        withAsterisk: true,
        placeholder: 'Status',
        value: '1',
        description: '',
        error: ''
    }

    const [data, setData] = React.useState({
        stockCategoryName: constStockCategoryName,
        stockCategoryIsActive: constStockCategoryIsActive,

        submitLoading: false,
        resetData: new Date(),

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        setData(d => ({
            ...d,
            stockCategoryName: {
                ...d.stockCategoryName,
                value: safeValue(stateData.stock_category_name, ''),
                error: '',
            },
            stockCategoryIsActive: {
                ...d.stockCategoryIsActive,
                value: safeValue(stateData.stock_category_is_active, '1'),
                error: '',
            },
        }))
    }, [stateData, data.resetData])

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const resetData = () => {
        setData(d => ({ ...d, resetData: new Date() }))
    }

    const editData = () => {
        setData(d => ({
            ...d,
            stockCategoryName: { ...d.stockCategoryName, error: '' },
        }));

        if (validator.isEmpty(data.stockCategoryName.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, stockCategoryName: { ...d.stockCategoryName, error: 'Kategori stok wajib diisi!', } }));
        } else {
            setData(d => ({ ...d, submitLoading: true }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('stock_category_id', stateData.stock_category_id);
            formDT.append('stock_category_name', data.stockCategoryName.value);
            formDT.append('stock_category_is_active', data.stockCategoryIsActive.value);

            axios({
                method: 'post',
                url: BE_API_ONE + '/edit_stock_category',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                if (response.data.flag_status === 'isset') {

                    fnSuccessEdit();

                    notifications.show({
                        id: notificationId.current.successSubmitData,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Edit Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'duplicate-name') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Nama kategori ${response.data.suspect} sudah pernah digunakan.`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: false,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Drawer
                opened={stateShow}
                onClose={fnShow}
                withCloseButton={false}
                position="left"
                closeOnClickOutside={true}

                title={
                    <Group noWrap spacing='xs'>
                        <ActionIcon
                            size='lg'
                            variant="light"
                            radius='xl'
                            color="blue"
                            onClick={fnShow}
                        >
                            <IconArrowLeft size='1.25rem' stroke={1.5} />
                        </ActionIcon>

                        <Text>Edit Kategori</Text>
                    </Group>
                }
                size='md'

                styles={() => ({
                    title: {
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }
                })}
            >
                <Box py='md'>

                    <Box mb='2rem'>
                        <InputComponent
                            stateId={data.stockCategoryName.id}
                            stateWithAsterisk={data.stockCategoryName.withAsterisk}
                            stateLabel={data.stockCategoryName.label}
                            stateDescription={data.stockCategoryName.description}
                            statePlaceHolder={data.stockCategoryName.placeholder}
                            stateValue={data.stockCategoryName.value}
                            stateError={data.stockCategoryName.error}

                            fnOnChange={(e) => setData(d => ({
                                ...d,
                                stockCategoryName: {
                                    ...d.stockCategoryName,
                                    value: e.target.value
                                }
                            }))}
                        />
                    </Box>

                    <Box mb='2rem'>
                        <InputWrapperCustom
                            stateLabel={data.stockCategoryIsActive.label}
                            stateWithAsterisk={data.stockCategoryIsActive.withAsterisk}
                            stateError={data.stockCategoryIsActive.error}
                            stateDescription={data.stockCategoryIsActive.description}
                        >
                            <SegmentedControl
                                fullWidth
                                value={data.stockCategoryIsActive.value}
                                onChange={(val) => setData(d => ({
                                    ...d,
                                    stockCategoryIsActive: {
                                        ...d.stockCategoryIsActive,
                                        value: val
                                    }
                                }))}
                                data={[
                                    { label: 'Aktif', value: '1' },
                                    { label: 'Non Aktif', value: '0' },
                                ]}
                            />
                        </InputWrapperCustom>
                    </Box>

                    <Group position="apart" noWrap spacing='xs' my='xl'>
                        <Button
                            fullWidth
                            variant="default"
                            color='gray'
                            onClick={resetData}
                        >
                            Reset
                        </Button>

                        <Button
                            fullWidth
                            loading={data.submitLoading}
                            onClick={editData}
                        >
                            Submit
                        </Button>
                    </Group>
                </Box>
            </Drawer>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />

        </React.Fragment>
    )
}

export default StockCategoryEdit;